import React, { Fragment, Component } from 'react';
import { motion } from 'framer-motion';
interface FrameworkNode {
  id: string;
  label: string;
  sublabel: string;
  icon: string;
}
const nodes: FrameworkNode[] = [{
  id: 'governance',
  label: 'Enterprise Governance',
  sublabel: 'Policy · Standards · Accountability',
  icon: '⬡'
}, {
  id: 'systems',
  label: 'Scalable Design Systems',
  sublabel: 'Tokens · Components · Documentation',
  icon: '⬡'
}, {
  id: 'experience',
  label: 'Human-Centered Experience',
  sublabel: 'Research · Accessibility · Usability',
  icon: '⬡'
}, {
  id: 'brand',
  label: 'Brand Cohesion',
  sublabel: 'Identity · Voice · Cultural Alignment',
  icon: '⬡'
}, {
  id: 'impact',
  label: 'Revenue & Cultural Impact',
  sublabel: 'Growth · Retention · Organizational Health',
  icon: '⬡'
}];
export function BridgeFramework() {
  return <section id="bridge-framework" className="py-24 lg:py-32 bg-[#F7F7F5]" aria-labelledby="bridge-heading" data-id="element-70">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-71">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-72">
          {/* Left — copy */}
          <div data-id="element-73">
            <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-74">
              <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-75" />
              Signature Framework
            </span>
            <h2 id="bridge-heading" className="font-heading font-bold text-[#1C1F23] text-3xl lg:text-4xl mb-6" data-id="element-76">
              The Bridge Framework
            </h2>
            <p className="font-body font-light text-[#4A5A66] leading-relaxed mb-6" data-id="element-77">
              I have developed a leadership framework that has transformed how
              organizations connect strategy, experience, and brand to
              real-world business impact. By combining human-centered design
              with systems thinking, this approach has built capabilities that
              last—empowering teams to deliver meaningful experiences,
              streamline operations, and strengthen brand influence across every
              touchpoint.
            </p>
            <p className="font-body font-light text-[#4A5A66] leading-relaxed mb-8" data-id="element-78">
              Applied across technology, retail, healthcare, finance, and
              multi-division enterprises, the framework has turned complexity
              into clarity and friction into opportunity:
            </p>

            {/* Principles */}
            <div className="space-y-3" data-id="element-79">
              {['Governance without systems has bred inefficiency; together they have enabled aligned action', 'Systems without experience have created friction; together they have delivered seamless interactions', 'Experience without brand has created confusion; together they have told a compelling story', 'Brand without impact has become a cost center; together they have driven measurable value'].map((principle, i) => <motion.div key={i} className="flex items-start gap-3" initial={{
              opacity: 0,
              x: -16
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              duration: 0.4
            }} data-id="element-80">
                  <span className="text-[#B08D57] font-heading font-bold text-sm mt-0.5 flex-shrink-0" data-id="element-81">
                    0{i + 1}
                  </span>
                  <p className="font-body text-[#4A5A66] text-sm italic" data-id="element-82">
                    {principle}
                  </p>
                </motion.div>)}
            </div>
          </div>

          {/* Right — visual diagram */}
          <div role="img" aria-label="Bridge Framework™ diagram: Enterprise Governance flows down through Scalable Design Systems, Human-Centered Experience, Brand Cohesion, to Revenue and Cultural Impact" data-id="element-83">
            <div className="relative flex flex-col items-center gap-0" data-id="element-84">
              {nodes.map((node, i) => <Fragment key={node.id} data-id="element-85">
                  {/* Node */}
                  <motion.div className="relative w-full max-w-sm" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.12,
                duration: 0.5
              }} data-id="element-86">
                    <div className={`relative border rounded-xl p-5 ${i === 0 ? 'bg-[#2F3A45] border-[#2F3A45] text-[#F7F7F5]' : i === nodes.length - 1 ? 'bg-[#B08D57] border-[#B08D57] text-[#1C1F23]' : 'bg-white border-[#D8D8D4] text-[#1C1F23]'}`} data-id="element-87">
                      <div className="flex items-center gap-3" data-id="element-88">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#B08D57]/20 text-[#B08D57]' : i === nodes.length - 1 ? 'bg-[#1C1F23]/20 text-[#1C1F23]' : 'bg-[#2F3A45]/10 text-[#2F3A45]'}`} aria-hidden="true" data-id="element-89">
                          {i + 1}
                        </div>
                        <div data-id="element-90">
                          <div className={`font-heading font-semibold text-sm ${i === 0 ? 'text-[#F7F7F5]' : i === nodes.length - 1 ? 'text-[#1C1F23]' : 'text-[#1C1F23]'}`} data-id="element-91">
                            {node.label}
                          </div>
                          <div className={`font-body text-xs mt-0.5 ${i === 0 ? 'text-[#C4D4DC]' : i === nodes.length - 1 ? 'text-[#2A3038]' : 'text-[#4A5A66]'}`} data-id="element-92">
                            {node.sublabel}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Connector */}
                  {i < nodes.length - 1 && <motion.div className="flex flex-col items-center py-1" initial={{
                opacity: 0,
                scaleY: 0
              }} whileInView={{
                opacity: 1,
                scaleY: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.12 + 0.1,
                duration: 0.3
              }} style={{
                originY: 0
              }} aria-hidden="true" data-id="element-93">
                      <div className="w-px h-6 bg-gradient-to-b from-[#B08D57] to-[#B08D57]/40" data-id="element-94" />
                      <div className="w-2 h-2 rotate-45 border-r-2 border-b-2 border-[#B08D57] -mt-1" data-id="element-95" />
                    </motion.div>}
                </Fragment>)}

              {/* Trademark */}
              <p className="mt-4 text-xs font-body text-[#4A5A66] text-center" data-id="element-96">
                The Bridge Framework — Michael K. Bigos
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}